import { useAuth } from '../../context/AuthContext';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Feb 2026 Pilot Readiness Sprint — replaces PilotBillingCountdownBanner.
 *
 * Shows a small banner when the manager is inside the 14-day free trial
 * and no payment method is on file. Fades to a subdued reminder in the
 * last 3 days, and switches to a warning tone on the last 2 days.
 *
 * If a payment method IS attached (or the manager already has an active
 * subscription), the banner renders nothing.
 */
export default function TrialCountdownBanner() {
  const { user } = useAuth();

  if (!user || user.role !== 'manager') return null;
  if (user.stripeSubscriptionId || user.paymentMethodId) return null;

  const trialEnd = user.trialEnd || user.trialEndsAt;
  if (!trialEnd) return null;

  const end = new Date(trialEnd);
  if (Number.isNaN(end.getTime())) return null;

  const now = new Date();
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysLeft = Math.ceil((end.getTime() - now.getTime()) / msPerDay);
  if (daysLeft > 14 || daysLeft < -30) return null;

  const isExpired = daysLeft < 0;
  const isUrgent = daysLeft >= 0 && daysLeft <= 2;
  const tone = isExpired
    ? 'bg-slate-900 text-white'
    : isUrgent
    ? 'bg-amber-100 border-amber-300 text-amber-900'
    : 'bg-emerald-50 border-emerald-200 text-emerald-900';

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4" data-testid="trial-countdown-banner">
      <div className={`rounded-2xl border p-4 flex items-start gap-3 ${tone}`}>
        {isExpired ? (
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        ) : (
          <Sparkles className="h-5 w-5 shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[14px] leading-tight" data-testid="trial-countdown-heading">
            {isExpired
              ? "Your free trial has ended — you're now on Pay-As-You-Go"
              : daysLeft === 0
              ? 'Last day of your free trial'
              : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left of your free trial`}
          </p>
          <p className="text-[13px] mt-0.5 opacity-90">
            {isExpired
              ? 'You can still cover shifts — you\u2019ll only be charged £3.99 per successfully completed shift, billed monthly. Add a card to switch to a subscription plan any time.'
              : 'Pick a plan and add a payment method any time before day 14, or roll into Pay-As-You-Go automatically.'}
          </p>
        </div>
        <Link
          to="/manager/billing"
          className="shrink-0 inline-flex items-center rounded-full bg-white/95 text-[#0B132B] hover:bg-white px-3 h-9 text-[13px] font-bold shadow-sm"
          data-testid="trial-countdown-cta"
        >
          Choose plan
        </Link>
      </div>
    </div>
  );
}
